import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAppData } from './AppDataContext';
import { useToast } from './ToastContext';
import { API_URL } from '../config/runtimeConfig';
import {
  BELL_NOTIFICATIONS_PAGE_SIZE,
  getStaffInAppNotifications,
  getStaffInAppNotificationUnreadCount,
  markAllStaffInAppNotificationsRead,
  markStaffChatNotificationsRead,
  markStaffInAppNotificationRead,
} from '../services/staffInAppNotificationService';
import {
  areNotificationSoundsEnabled,
  playNotificationSound,
  unlockNotificationSounds,
} from '../utils/staffNotificationSounds';
import { isStaffWorkspacePath, isWaitlistNotification, isAppointmentConfirmationRequestedNotification, parseStaffNotificationPayload, getStaffNotificationTitle, getStaffNotificationBody } from '../utils/staffNotificationNavigation';
import { syncAppBadge } from '../utils/appBadge';
import WaitlistStaffAlertDialog from '../components/waitlist/WaitlistStaffAlertDialog';
import AppointmentConfirmationAlertDialog from '../components/appointments/AppointmentConfirmationAlertDialog';

const StaffRealtimeContext = createContext(null);

const resolveHubUrl = () => {
  const apiBase = (API_URL || '').replace(/\/$/, '');
  const origin = apiBase.replace(/\/api$/i, '');
  return `${origin}/hubs/store-realtime`;
};

const sameId = (left, right) => {
  if (!left || !right) return false;
  return String(left).toLowerCase() === String(right).toLowerCase();
};

export const StaffRealtimeProvider = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isManagementAuthenticated, managementUser } = useAuth();
  const { currentStore } = useAppData();
  const { showInfo } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [badgeCount, setBadgeCount] = useState(null);
  const [soundsEnabled, setSoundsEnabledState] = useState(areNotificationSoundsEnabled());
  const [connectionState, setConnectionState] = useState('disconnected');
  const [waitlistAlert, setWaitlistAlert] = useState(null);
  const [confirmationAlert, setConfirmationAlert] = useState(null);
  const connectionRef = useRef(null);
  const calendarListenersRef = useRef(new Set());
  const storeIdRef = useRef(currentStore?.id);
  const seenNotificationIdsRef = useRef(new Set());
  const unreadCountRef = useRef(0);
  const watchedChatIdRef = useRef(null);
  const acknowledgeSeqRef = useRef(0);
  const acknowledgeChatRef = useRef(() => undefined);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const isStaffWorkspace = isStaffWorkspacePath(location.pathname);
  const isStaffWorkspaceRef = useRef(isStaffWorkspace);

  storeIdRef.current = currentStore?.id;
  unreadCountRef.current = unreadCount;
  showInfoRef.current = showInfo;
  tRef.current = t;
  isStaffWorkspaceRef.current = isStaffWorkspace;

  const managementUserId = managementUser?.id || managementUser?.userId || null;
  const userType = managementUser?.userType || managementUser?.user_type;
  const isAppAdmin = userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin';
  const isAppAdminRef = useRef(isAppAdmin);
  isAppAdminRef.current = isAppAdmin;

  const notifyCalendarListeners = useCallback((change) => {
    if (!change) return;
    const activeStoreId = storeIdRef.current;
    if (activeStoreId && change.storeId && !sameId(change.storeId, activeStoreId)) {
      return;
    }
    calendarListenersRef.current.forEach((listener) => {
      try {
        listener(change);
      } catch (error) {
        console.error('Calendar change listener failed', error);
      }
    });
  }, []);

  const joinStoreGroup = useCallback(async (connection, storeId) => {
    if (!connection || !storeId) return;
    try {
      await connection.invoke('JoinStore', storeId);
    } catch (error) {
      console.error('Failed to join store realtime group', error);
    }
  }, []);

  const refreshNotifications = useCallback(async (storeId) => {
    if (!storeId) {
      setNotifications([]);
      setUnreadCount(0);
      setBadgeCount(0);
      seenNotificationIdsRef.current = new Set();
      return;
    }

    try {
      const [page, unread] = await Promise.all([
        getStaffInAppNotifications(storeId, {
          pageIndex: 0,
          itemsPerPage: BELL_NOTIFICATIONS_PAGE_SIZE,
        }),
        getStaffInAppNotificationUnreadCount(storeId),
      ]);
      const list = Array.isArray(page?.data) ? page.data : [];
      seenNotificationIdsRef.current = new Set(
        list.map((item) => String(item.id || '').toLowerCase()).filter(Boolean)
      );
      setNotifications(list);
      setUnreadCount(Number.isInteger(unread?.count) ? unread.count : 0);
      setBadgeCount(Number.isInteger(unread?.badgeCount) ? unread.badgeCount : 0);
    } catch (error) {
      console.error('Failed to load staff notifications', error);
    }
  }, []);

  useEffect(() => {
    if (!isManagementAuthenticated || !currentStore?.id) {
      setNotifications([]);
      setUnreadCount(0);
      if (!isManagementAuthenticated) setBadgeCount(null);
      return undefined;
    }

    refreshNotifications(currentStore.id);
    return undefined;
  }, [isManagementAuthenticated, currentStore?.id, refreshNotifications]);

  useEffect(() => {
    if (!isManagementAuthenticated || !managementUserId) {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
      }
      setConnectionState('disconnected');
      return undefined;
    }

    const token = localStorage.getItem('management_auth_token');
    if (!token) {
      return undefined;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), {
        accessTokenFactory: () => localStorage.getItem('management_auth_token') || '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('ReceiveNotification', (notification) => {
      if (!notification) return;

      const activeStoreId = storeIdRef.current;
      const payload = parseStaffNotificationPayload(notification.payloadJson);
      const payloadConversationId = payload.conversationId;
      const storeMatches = !activeStoreId || !notification.storeId || sameId(notification.storeId, activeStoreId);
      const chatIsOpen = sameId(payloadConversationId, watchedChatIdRef.current)
        && (isAppAdminRef.current || (Boolean(activeStoreId) && storeMatches));
      const hasAbsoluteBadge = Number.isInteger(notification.badgeCount) && notification.badgeCount > 0;
      if (hasAbsoluteBadge && !chatIsOpen) {
        setBadgeCount(notification.badgeCount);
      }

      if (!isAppAdminRef.current && activeStoreId && notification.storeId && !sameId(notification.storeId, activeStoreId)) {
        return;
      }

      const notificationId = String(notification.id || '').toLowerCase();
      if (!notificationId || seenNotificationIdsRef.current.has(notificationId)) {
        return;
      }
      seenNotificationIdsRef.current.add(notificationId);

      const incoming = chatIsOpen ? { ...notification, isRead: true } : notification;
      setNotifications((prev) => [incoming, ...prev].slice(0, BELL_NOTIFICATIONS_PAGE_SIZE));
      if (chatIsOpen) {
        acknowledgeChatRef.current(payloadConversationId);
        return;
      }
      setUnreadCount((count) => count + 1);
      if (!hasAbsoluteBadge) {
        setBadgeCount((count) => (Number.isInteger(count) ? count + 1 : 1));
      }
      const isWaitlist = isWaitlistNotification(notification);
      const isConfirmationRequested = isAppointmentConfirmationRequestedNotification(notification);
      if (!isWaitlist && !isConfirmationRequested) {
        const title = getStaffNotificationTitle(notification, tRef.current);
        const body = payload.message ? getStaffNotificationBody(notification, tRef.current) : '';
        showInfoRef.current(body ? `${title}: ${body}` : title);
      }
      if ((!isWaitlist && !isConfirmationRequested) || isStaffWorkspaceRef.current) {
        playNotificationSound(notification.type);
      }

      const notificationType = String(notification.type ?? '');
      if (notificationType !== 'SupportChat' && notificationType !== '3' && !isWaitlist) {
        notifyCalendarListeners({
          storeId: notification.storeId,
          appointmentId: notification.appointmentId,
          changeType: 'Created',
        });
      }
    });

    connection.on('AppointmentCalendarChanged', (change) => {
      notifyCalendarListeners(change);
    });

    const showWaitlistAlertIfStaffWorkspace = (alert) => {
      if (!alert || !isStaffWorkspaceRef.current) return;
      const activeStoreId = storeIdRef.current;
      if (activeStoreId && alert.storeId && !sameId(alert.storeId, activeStoreId)) {
        return;
      }
      setWaitlistAlert(alert);
      playNotificationSound(alert.alertType);
    };

    connection.on('ReceiveWaitlistJoined', showWaitlistAlertIfStaffWorkspace);
    connection.on('ReceiveWaitlistSlotOpened', showWaitlistAlertIfStaffWorkspace);
    connection.on('ReceiveAppointmentConfirmationRequested', (alert) => {
      if (!alert || !isStaffWorkspaceRef.current) return;
      const activeStoreId = storeIdRef.current;
      if (activeStoreId && alert.storeId && !sameId(alert.storeId, activeStoreId)) {
        return;
      }
      setConfirmationAlert(alert);
      playNotificationSound('AppointmentConfirmationRequested');
    });
    connection.onreconnecting(() => setConnectionState('reconnecting'));
    connection.onreconnected(() => {
      setConnectionState('connected');
      if (storeIdRef.current) {
        joinStoreGroup(connection, storeIdRef.current);
        refreshNotifications(storeIdRef.current);
      }
    });
    connection.onclose(() => setConnectionState('disconnected'));

    connectionRef.current = connection;
    setConnectionState('connecting');
    connection
      .start()
      .then(async () => {
        setConnectionState('connected');
        if (storeIdRef.current) {
          await joinStoreGroup(connection, storeIdRef.current);
        }
      })
      .catch((error) => {
        console.error('SignalR connection failed', error);
        setConnectionState('disconnected');
      });

    return () => {
      connection.stop().catch(() => {});
      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }
    };
  }, [
    isManagementAuthenticated,
    managementUserId,
    refreshNotifications,
    notifyCalendarListeners,
    joinStoreGroup,
  ]);

  useEffect(() => {
    if (!isStaffWorkspace) {
      setWaitlistAlert(null);
      setConfirmationAlert(null);
    }
  }, [isStaffWorkspace]);

  useEffect(() => {
    const connection = connectionRef.current;
    const storeId = currentStore?.id;
    if (!storeId || !connection || connection.state !== signalR.HubConnectionState.Connected) {
      return undefined;
    }

    joinStoreGroup(connection, storeId);
    return undefined;
  }, [currentStore?.id, connectionState, joinStoreGroup]);

  useEffect(() => {
    if (!isStaffWorkspace) return undefined;
    if (!isManagementAuthenticated) {
      void syncAppBadge(0);
      return undefined;
    }
    if (!Number.isInteger(badgeCount)) return undefined;
    void syncAppBadge(badgeCount);
    return undefined;
  }, [isStaffWorkspace, isManagementAuthenticated, badgeCount]);

  useEffect(() => {
    if (!isManagementAuthenticated) return undefined;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (storeIdRef.current) refreshNotifications(storeIdRef.current);
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isManagementAuthenticated, refreshNotifications]);

  const subscribeToCalendarChanges = useCallback((listener) => {
    calendarListenersRef.current.add(listener);
    return () => {
      calendarListenersRef.current.delete(listener);
    };
  }, []);

  const enableSounds = useCallback(async () => {
    const unlocked = await unlockNotificationSounds();
    setSoundsEnabledState(areNotificationSoundsEnabled() || unlocked);
    return unlocked || areNotificationSoundsEnabled();
  }, []);

  const markRead = useCallback(async (notificationId) => {
    const storeId = storeIdRef.current;
    if (!storeId || !notificationId) return;

    await markStaffInAppNotificationRead(notificationId, storeId);
    setNotifications((prev) =>
      prev.map((item) => (sameId(item.id, notificationId) ? { ...item, isRead: true } : item))
    );
    setUnreadCount((count) => Math.max(0, count - 1));
    setBadgeCount((count) => (Number.isInteger(count) ? Math.max(0, count - 1) : count));
  }, []);

  const acknowledgeChat = useCallback(async (conversationId) => {
    watchedChatIdRef.current = conversationId || null;
    const storeId = storeIdRef.current;
    if (!storeId || !conversationId) return;
    const seq = acknowledgeSeqRef.current + 1;
    acknowledgeSeqRef.current = seq;
    try {
      const unread = await markStaffChatNotificationsRead(conversationId, storeId);
      if (seq !== acknowledgeSeqRef.current) return;
      if (!sameId(watchedChatIdRef.current, conversationId)) return;
      setNotifications((prev) => prev.map((item) => {
        const payloadConversationId = parseStaffNotificationPayload(item.payloadJson).conversationId;
        return sameId(payloadConversationId, conversationId) ? { ...item, isRead: true } : item;
      }));
      setUnreadCount(unread.count);
      setBadgeCount(unread.badgeCount);
    } catch {
      // The next refresh restores the badge if this clear did not land.
    }
  }, []);
  acknowledgeChatRef.current = acknowledgeChat;

  const markAllRead = useCallback(async () => {
    const storeId = storeIdRef.current;
    if (!storeId) return;

    await markAllStaffInAppNotificationsRead(storeId);
    const cleared = unreadCountRef.current;
    setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    setUnreadCount(0);
    setBadgeCount((count) => (Number.isInteger(count) ? Math.max(0, count - cleared) : count));
  }, []);

  const openWaitlistAlert = useCallback((alert) => {
    if (!alert) return;
    setWaitlistAlert(alert);
  }, []);

  const openConfirmationAlert = useCallback((alert) => {
    if (!alert) return;
    setConfirmationAlert(alert);
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      soundsEnabled,
      connectionState,
      enableSounds,
      markRead,
      markAllRead,
      acknowledgeChat,
      openWaitlistAlert,
      openConfirmationAlert,
      refreshNotifications,
      subscribeToCalendarChanges,
    }),
    [
      notifications,
      unreadCount,
      soundsEnabled,
      connectionState,
      enableSounds,
      markRead,
      markAllRead,
      acknowledgeChat,
      openWaitlistAlert,
      openConfirmationAlert,
      refreshNotifications,
      subscribeToCalendarChanges,
    ]
  );

  return (
    <StaffRealtimeContext.Provider value={value}>
      {children}
      <WaitlistStaffAlertDialog
        alert={isStaffWorkspace ? waitlistAlert : null}
        onClose={() => setWaitlistAlert(null)}
        storeId={waitlistAlert?.storeId || currentStore?.id}
        storeCurrency={currentStore?.currency}
      />
      <AppointmentConfirmationAlertDialog
        alert={isStaffWorkspace ? confirmationAlert : null}
        onClose={() => setConfirmationAlert(null)}
        storeId={confirmationAlert?.storeId || currentStore?.id}
      />
    </StaffRealtimeContext.Provider>
  );
};
export const useStaffRealtime = () => {
  const context = useContext(StaffRealtimeContext);
  if (!context) {
    throw new Error('useStaffRealtime must be used within a StaffRealtimeProvider');
  }
  return context;
};
